import { zodResolver } from '@hookform/resolvers/zod';
import {
  CreateWebsiteCategoryPageRequestSchema,
  categoryPageUsesCuratedCircles,
  slugify,
  type CreateWebsiteCategoryPageRequest,
  type WebsitePublishStatus,
} from '@utazzo/shared';
import { UzButton, UzForm, UzFormField, UzInput, UzTabs } from '@utazzo/ui';
import { ArrowLeft, Wand2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useForm, useWatch, type Resolver } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import {
  CREATE_DEFAULTS,
  firstErrorTab,
  TAB_LABEL,
  toForm,
  toPayload,
  type DetailTab,
} from '../category-page-form';
import { categoryPublicUrl } from '../category-page-url';
import { BestSellersEditor } from '../components/BestSellersEditor';
import { CircleDestinationsEditor } from '../components/CircleDestinationsEditor';
import { DestinationSortingEditor } from '../components/DestinationSortingEditor';
import { FaqsEditor } from '../components/FaqsEditor';
import { ReviewsEditor } from '../components/ReviewsEditor';
import { SeoLinksEditor } from '../components/SeoLinksEditor';
import {
  useCategoryPageDetail,
  useCreateCategoryPage,
  usePublishCategoryPage,
  useUnpublishCategoryPage,
  useUpdateCategoryPage,
} from '../use-category-pages';

import { useSubmitForReview } from '@/features/website-approvals/use-website-approvals';
import { MediaDropField } from '@/features/website-cms/components/MediaDropField';
import { RichTextEditor } from '@/features/website-cms/components/RichTextEditor';
import { useListReturn } from '@/hooks/use-list-return';

const STATUS_META: Record<WebsitePublishStatus, { dot: string; label: string }> = {
  DRAFT: { dot: 'bg-zinc-400', label: 'Draft' },
  PENDING_REVIEW: { dot: 'bg-amber-500', label: 'In review' },
  PUBLISHED: { dot: 'bg-emerald-500', label: 'Published' },
  ARCHIVED: { dot: 'bg-rose-500', label: 'Archived' },
};

export const CategoryPageDetailPage = (): React.JSX.Element => {
  const { id } = useParams();
  const navigate = useNavigate();
  const backToList = useListReturn('/category-pages');
  const isNew = id === 'new';

  const detailQuery = useCategoryPageDetail(isNew ? undefined : id);
  const page = detailQuery.data ?? null;

  const createPage = useCreateCategoryPage();
  const updatePage = useUpdateCategoryPage();
  const publishPage = usePublishCategoryPage();
  const submitForReview = useSubmitForReview();
  const unpublishPage = useUnpublishCategoryPage();

  const [activeTab, setActiveTab] = useState<DetailTab>('details');

  const form = useForm<CreateWebsiteCategoryPageRequest>({
    resolver: zodResolver(
      CreateWebsiteCategoryPageRequestSchema,
    ) as Resolver<CreateWebsiteCategoryPageRequest>,
    defaultValues: CREATE_DEFAULTS,
  });

  useEffect(() => {
    if (isNew) form.reset(CREATE_DEFAULTS);
    else if (page) form.reset(toForm(page));
  }, [isNew, page, form]);

  const pending =
    createPage.isPending ||
    updatePage.isPending ||
    publishPage.isPending ||
    unpublishPage.isPending;
  const status = page ? STATUS_META[page.status] : null;
  const slug = useWatch({ control: form.control, name: 'slug' }) ?? '';
  const showCircleDestinations = categoryPageUsesCuratedCircles(slug);

  useEffect(() => {
    if (!showCircleDestinations && activeTab === 'circleDestinations') {
      setActiveTab('details');
    }
  }, [showCircleDestinations, activeTab]);

  const submit = form.handleSubmit(
    async (values) => {
      const payload = toPayload(values);
      if (isNew) {
        const created = await createPage.mutateAsync(payload);
        navigate(`/category-pages/${created.id}`);
      } else if (page) {
        await updatePage.mutateAsync({ id: page.id, payload });
        form.reset(values);
      }
    },
    (errors) => {
      const tab = firstErrorTab(Object.keys(errors));
      setActiveTab(tab);
      toast.error(`Please fix the highlighted fields on the “${TAB_LABEL[tab]}” tab.`);
    },
  );

  const tabs = useMemo(
    () => [
      {
        value: 'details' as const,
        label: 'Details',
        content: (
          <div className="grid items-start gap-4 max-sm:grid-cols-1 lg:grid-cols-2">
            <UzFormField name="title">
              {({ field, error }) => (
                <UzInput
                  {...field}
                  value={(field.value as string) ?? ''}
                  label="Title"
                  placeholder="India Tour Packages"
                  required
                  error={error}
                  disabled={pending}
                />
              )}
            </UzFormField>
            <UzFormField name="slug">
              {({ field, error }) => (
                <UzInput
                  {...field}
                  value={(field.value as string) ?? ''}
                  label="Slug"
                  placeholder="india-tour-packages"
                  hint="Public URL: /{slug}"
                  required
                  error={error}
                  disabled={pending}
                  rightAdornment={
                    <button
                      type="button"
                      title="Generate slug from title"
                      aria-label="Generate slug from title"
                      disabled={pending}
                      onClick={() => field.onChange(slugify(form.getValues('title') ?? ''))}
                      className="text-zinc-400 transition-colors hover:text-zinc-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <Wand2 className="h-4 w-4" />
                    </button>
                  }
                />
              )}
            </UzFormField>
            <UzFormField name="heroHeading">
              {({ field, error }) => (
                <UzInput
                  {...field}
                  value={(field.value as string | null) ?? ''}
                  label="Hero heading"
                  error={error}
                  disabled={pending}
                />
              )}
            </UzFormField>
            <UzFormField name="heroSubheading">
              {({ field, error }) => (
                <UzInput
                  {...field}
                  value={(field.value as string | null) ?? ''}
                  label="Hero subheading"
                  error={error}
                  disabled={pending}
                />
              )}
            </UzFormField>
            <UzFormField name="heroMediaId">
              {({ field }) => (
                <div className="space-y-1 lg:col-span-2">
                  <label className="text-sm font-medium text-zinc-700">Hero image</label>
                  <p className="text-xs text-zinc-500">
                    Fallback when no hero video is set, and poster while the video loads.
                  </p>
                  <MediaDropField
                    value={(field.value as string | null) ?? null}
                    kind="image"
                    folder="category-pages"
                    disabled={pending}
                    onChange={(v) => field.onChange(v)}
                  />
                </div>
              )}
            </UzFormField>
            <UzFormField name="heroVideoId">
              {({ field }) => (
                <div className="space-y-1 lg:col-span-2">
                  <label className="text-sm font-medium text-zinc-700">Hero video</label>
                  <p className="text-xs text-zinc-500">Optional. Plays behind the hero when set.</p>
                  <MediaDropField
                    value={(field.value as string | null) ?? null}
                    kind="video"
                    folder="category-pages"
                    disabled={pending}
                    onChange={(v) => field.onChange(v)}
                  />
                </div>
              )}
            </UzFormField>
          </div>
        ),
      },
      {
        value: 'about' as const,
        label: 'About',
        content: (
          <UzFormField name="aboutHtml">
            {({ field }) => (
              <RichTextEditor
                value={(field.value as string | null) ?? ''}
                onChange={field.onChange}
                label="About this category"
                folder="category-pages"
                disabled={pending}
              />
            )}
          </UzFormField>
        ),
      },
      {
        value: 'bestSellers' as const,
        label: 'Best sellers',
        content: <BestSellersEditor form={form} disabled={pending} />,
      },
      ...(showCircleDestinations
        ? [
            {
              value: 'circleDestinations' as const,
              label: 'Destination circles',
              content: <CircleDestinationsEditor form={form} disabled={pending} />,
            },
          ]
        : []),
      {
        value: 'faq' as const,
        label: 'FAQ',
        content: <FaqsEditor form={form} disabled={pending} />,
      },
      {
        value: 'seoLinks' as const,
        label: 'SEO links',
        content: <SeoLinksEditor form={form} disabled={pending} />,
      },
      {
        value: 'reviews' as const,
        label: 'Reviews',
        content: <ReviewsEditor form={form} disabled={pending} />,
      },
      {
        value: 'seo' as const,
        label: 'SEO',
        content: (
          <div className="max-w-2xl space-y-4">
            <UzFormField name="metaTitle">
              {({ field, error }) => (
                <UzInput
                  {...field}
                  value={(field.value as string | null) ?? ''}
                  label="Meta title"
                  error={error}
                  disabled={pending}
                />
              )}
            </UzFormField>
            <UzFormField name="metaDescription">
              {({ field, error }) => (
                <UzInput
                  {...field}
                  value={(field.value as string | null) ?? ''}
                  label="Meta description"
                  error={error}
                  disabled={pending}
                />
              )}
            </UzFormField>
            <UzFormField name="metaKeywords">
              {({ field, error }) => (
                <UzInput
                  {...field}
                  value={(field.value as string | null) ?? ''}
                  label="Meta keywords"
                  error={error}
                  disabled={pending}
                />
              )}
            </UzFormField>
            <UzFormField name="ogImageId">
              {({ field }) => (
                <div className="space-y-1">
                  <label className="text-sm font-medium text-zinc-700">
                    Social share image (OG)
                  </label>
                  <MediaDropField
                    value={(field.value as string | null) ?? null}
                    kind="image"
                    folder="category-pages"
                    disabled={pending}
                    onChange={(v) => field.onChange(v)}
                  />
                </div>
              )}
            </UzFormField>
          </div>
        ),
      },
      // 2026-08-01. Ordering of the destination tiles on this category page.
      // Sits after SEO so the content tabs stay grouped.
      {
        value: 'sorting' as const,
        label: 'Sorting',
        content: (
          <DestinationSortingEditor
            form={form}
            pageId={isNew ? undefined : page?.id}
            disabled={pending}
          />
        ),
      },
    ],
    [form, pending, showCircleDestinations, isNew, page?.id],
  );

  return (
    <div className="-mx-4 -mt-6 flex min-h-full flex-col lg:-mx-8 lg:-mt-8">
      <div className="sticky -top-6 z-30 flex h-12 items-center gap-3 border-b border-zinc-200 bg-white px-6 max-sm:h-auto max-sm:flex-wrap max-sm:gap-x-2 max-sm:gap-y-1.5 max-sm:px-4 max-sm:py-2 lg:-top-8 lg:px-8">
        <button
          type="button"
          onClick={backToList}
          className="-ml-1 inline-flex h-7 w-7 items-center justify-center rounded text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900"
          aria-label="Back"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div className="flex min-w-0 items-center gap-2 max-sm:max-w-[calc(100%-2.5rem)]">
          <span className="text-xs font-medium text-zinc-500 max-sm:shrink-0 max-sm:whitespace-nowrap">
            Category pages
          </span>
          <span className="text-zinc-300">/</span>
          <span className="truncate text-sm font-semibold text-zinc-900">
            {isNew ? 'New page' : (page?.title ?? 'Page')}
          </span>
        </div>
        <span className="flex-1 max-sm:basis-full" />
        {status && (
          <span className="inline-flex items-center gap-1.5 text-xs text-zinc-600">
            <span className={`h-1.5 w-1.5 rounded-full ${status.dot}`} />
            {status.label}
          </span>
        )}
        {/* Opt-in review: publish stays available to everyone who has it, and
            this is the "let the manager sign off first" alternative. */}
        {!isNew && page && page.status !== 'PENDING_REVIEW' && (
          <UzButton
            variant="ghost"
            size="sm"
            onClick={() => submitForReview.mutate({ type: 'CATEGORY_PAGE', id: page.id })}
            loading={submitForReview.isPending}
            disabled={pending}
          >
            Submit for review
          </UzButton>
        )}
        {!isNew && page && page.status !== 'PUBLISHED' && (
          <UzButton
            variant="ghost"
            size="sm"
            onClick={() => publishPage.mutate(page.id)}
            loading={publishPage.isPending}
            disabled={pending}
          >
            Publish
          </UzButton>
        )}
        {!isNew && page && page.status === 'PUBLISHED' && (
          <UzButton
            variant="ghost"
            size="sm"
            onClick={() => unpublishPage.mutate(page.id)}
            loading={unpublishPage.isPending}
            disabled={pending}
          >
            Unpublish
          </UzButton>
        )}
        <UzButton variant="ghost" size="sm" onClick={backToList} disabled={pending}>
          Cancel
        </UzButton>
        <UzButton size="sm" onClick={submit} loading={pending}>
          {isNew ? 'Create' : 'Save'}
        </UzButton>
      </div>
      <div className="px-6 py-6 lg:px-8">
        {!isNew && page && page.status !== 'PUBLISHED' && (
          <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
            <p className="font-medium">This page is not live yet</p>
            <p className="mt-1 text-amber-900">
              Draft and archived pages return 404 on the website. Click <strong>Publish</strong> to
              make <code className="rounded bg-amber-100 px-1">/{page.slug}</code> available at{' '}
              <a
                href={categoryPublicUrl(page.slug)}
                rel="noreferrer"
                className="font-medium underline max-sm:break-all"
              >
                {categoryPublicUrl(page.slug)}
              </a>
              .
            </p>
          </div>
        )}
        <UzForm form={form} onSubmit={submit}>
          <UzTabs
            value={activeTab}
            onValueChange={(v) => setActiveTab(v as DetailTab)}
            tabs={tabs}
            // Phones: the strip scrolls sideways instead of running off-screen.
            listClassName="max-sm:max-w-full max-sm:justify-start max-sm:overflow-x-auto"
          />
        </UzForm>
      </div>
    </div>
  );
};
